import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Popover } from 'radix-ui';
import { nanoid } from 'nanoid';
import { useDatadogLog } from '../../logging';
import { useTranslation } from 'react-i18next';
import MaterialSymbol from '../../../DataDisplay/MaterialSymbol/MaterialSymbol';
import Icon, { IconProps } from '../../../DataDisplay/Icon/Icon';
import styles from './Select2.module.scss';
import useFocusTrap from '../../../../hooks/useFocusTrap';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import ErrorText from '../../../Text/ErrorText/ErrorText';
import { withStyles } from '../../../../utils/utils';
import SelectOption, { type SelectOptionProps } from '../../SelectOption/SelectOption';
import Skeleton from '../../../Utility/Skeleton/Skeleton';

/**
 * TODO:
 * - selectRef (for things like useFocusTrap, useClickOutside, etc)
 * - option grouping (<optgroup />)
 * - refactor any reusable things into their own modules
 * - create a portal for the dropdown
 */

export type SelectFilterEvent<Data> = {
  filter: string;
  results: Data[];
  preventDefault: () => void;
  debounce: (cb: () => void, ms?: number) => void;
};

const cn = withStyles(styles);

export type DefaultOptionProps = {
  value?: string;
  label?: string;
} & object;

export type OptionContainerProps = Pick<SelectOptionProps, 'className' | 'children' | 'style'> & {
  onClick?: () => void;
};

export type SelectRenderOptionArgs = {
  Option: React.ComponentType<OptionContainerProps>;
  SelectedIcon: React.ComponentType<IconProps>;
  focused: boolean;
  selected: boolean;
  disabled: boolean;
  preventDefault: () => void;
  close: () => void;
  filter: string;
};

export type Select2Props<Data extends DefaultOptionProps = Record<string, never>> = {
  name: string;
  options: Data[];
  optionValue?: (o: Data) => string;
  optionLabel?: (o: Data) => string;
  disableOptionWhen?: (o: Data) => boolean;
  renderIcon?: (o: Data | null, args: { Icon: typeof Icon }) => React.ReactNode;
  showClear?: boolean;
  placeholder?: string;
  value?: string;
  errorMsg?: string;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  emptyFilterMsg?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    args: { data: Data | null; label: string; value: string },
  ) => void;
  limit?: number;
  readOnly?: boolean;
  style?: {
    '--triggerBgColor'?: string;
    '--triggerBoxShadow'?: string;
    '--triggerBorderRadius'?: string;
    '--triggerFontSize'?: string;
  };
  alignDropdown?: 'start' | 'center' | 'end';
  /**
   * `auto` (default) lets the menu grow to its widest option; `trigger` caps it at the trigger's
   * width and wraps long labels instead, for menus that must stay inside a narrow container such
   * as a side drawer.
   */
  dropdownWidth?: 'auto' | 'trigger';
  renderDropdownHeader?: ({ close }: { close: () => void }) => React.ReactNode;
  renderDropdownFooter?: ({ close }: { close: () => void }) => React.ReactNode;
  renderOption?: (o: Data, args: SelectRenderOptionArgs) => React.ReactNode;
  renderSelectedValue?: (
    o: Data | null,
    args: {
      value: string;
      label: string;
      Placeholder: React.ComponentType<{ children?: React.ReactNode }>;
      disabled: boolean;
    },
  ) => React.ReactNode;
  filterable?: boolean;
  filterOn?: (o: Data, args: { query: string }) => boolean;
  onFilter?: (e: SelectFilterEvent<Data>) => void;
  sort?: boolean;
  sortOn?: (o: Data) => string | number;
  sortOrder?: 'asc' | 'desc';
};

function Select2<Data extends DefaultOptionProps = Record<string, never>>({
  options,
  optionValue,
  optionLabel,
  placeholder = 'Select...',
  showClear = false,
  name,
  value = '',
  errorMsg,
  disabled: selectDisabled = false,
  loading = false,
  renderIcon,
  id,
  emptyFilterMsg = 'No results found',
  limit: limitProp,
  onBlur = () => {},
  onChange = () => {},
  onFocus = () => {},
  readOnly = false,
  alignDropdown = 'start',
  dropdownWidth = 'auto',
  renderDropdownHeader,
  renderDropdownFooter,
  renderOption,
  disableOptionWhen,
  renderSelectedValue,
  filterable = false,
  onFilter,
  filterOn,
  sort = true,
  sortOn,
  sortOrder = 'asc',
  style,
}: Select2Props<Data>) {
  const dropdownId = useRef(id || nanoid());
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // We need currentValue so that we can watch it; we pass it into a hidden
  // input, and when it changes value, it will fire a native onChange event.
  // Hacky.
  const [curr, setCurr] = useState<string>('');
  const selectedOption = useMemo(() => options.find((o) => getValue(o) === curr) || null, [curr, options]);
  const [focusedOption, setFocusedOption] = useState<Data | null>(null);
  const [filtered, setFiltered] = useState(options);
  const limit = useMemo(() => limitProp || filtered.length, [limitProp, filtered]);
  const [offset, setOffset] = useState(1);
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation('components');

  const finalOptions = useMemo(
    () => [...filtered].sort(sortFn).slice(0, offset * limit),
    [filtered, offset, limit, sortOn, sort, sortOrder],
  );

  const { trapFocus } = useFocusTrap({
    active: isOpen,
  });

  const log = useDatadogLog();

  useOutsideClick([containerRef, dropdownRef], () => {
    if (isOpen) {
      handleBlur();
      toggleMenu(false);
    }
  });

  useEffect(() => {
    if (!isOpen) return;

    if (!focusedOption) return;

    const el = document.getElementById(getOptionId(focusedOption));

    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedOption, finalOptions]);

  useEffect(() => {
    setFiltered(options);
  }, [options]);

  useEffect(() => {
    if (!isOpen) return () => {};

    const filteredOptions = options.filter((o) => {
      if (filterOn) return filterOn(o, { query: filter });

      return getLabelText(o).toLowerCase().includes(filter.toLowerCase());
    });

    let timeoutId: number;

    const debounce = (cb: () => void, ms = 1000) => {
      timeoutId = window.setTimeout(cb, ms);
    };

    if (onFilter) {
      let shouldOverrideFiltering = false;

      const preventDefault = () => {
        shouldOverrideFiltering = true;
      };

      onFilter({ filter, results: filteredOptions, preventDefault, debounce });

      if (!shouldOverrideFiltering) {
        setFiltered(filteredOptions);
      }
    } else {
      setFiltered(filteredOptions);
    }

    return () => clearTimeout(timeoutId);
  }, [filter]);

  useEffect(() => {
    if (focusedOption && !finalOptions.includes(focusedOption)) {
      setFocusedOption(finalOptions[0]);
    }
  }, [finalOptions]);

  useEffect(() => {
    if (isOpen) {
      setOffset(1);
      setFilter('');
      setFocusedOption(selectedOption);
    } else {
      // set to null because keyboard event handlers keep firing after the dropdown is closed
      setFocusedOption(null);
    }
  }, [isOpen]);

  // Close dropdown when browser navigation occurs (back/forward buttons)
  useEffect(() => {
    if (!isOpen) return () => {};

    const handlePopState = () => {
      toggleMenu(false);
      handleBlur();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);

  useEffect(() => {
    handleChange(value);
  }, [value]);

  useEffect(() => {
    if (curr === value) return;

    if (!hiddenInputRef.current) return;
    hiddenInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  }, [curr]);

  // call .blur() on select, and then the onBlur in the <select> will pick it up and emit the
  // React Synthetic event, as if we were using a regular <select> (hopefully)
  function handleBlur() {
    if (!hiddenInputRef.current) return;

    // blur only seems to work if we're already focused
    hiddenInputRef.current.focus();
    hiddenInputRef.current.blur();
  }

  function toggleMenu(opened: boolean) {
    if (!opened) {
      setIsOpen(false);

      return;
    }

    if (selectDisabled || readOnly) return;

    setFilter('');
    setIsOpen(true);
  }

  function handleChange(newValue: string | null) {
    if (curr === newValue) return;

    setCurr(newValue || '');

    if (!hiddenInputRef.current) return;

    // Get the native 'value' setter from the HTMLInputElement prototype
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    )?.set?.bind(hiddenInputRef.current);

    if (nativeInputValueSetter) {
      // Call the native value setter with the new value
      nativeInputValueSetter.call(hiddenInputRef.current, newValue);

      const event = new Event('change', { bubbles: true });
      hiddenInputRef.current.dispatchEvent(event);
      toggleMenu(false);
    }
  }

  function getSelectedLabelText() {
    if (!curr) return placeholder;

    if (selectedOption) return getLabelText(selectedOption);

    return curr;
  }

  const handleKeyDownOpenDialog = (e: React.KeyboardEvent) => {
    const SKIP_AMOUNT = 5;

    if (!isOpen) {
      switch (e.key) {
        case 'Up':
        case 'ArrowUp':
        case 'Down':
        case 'ArrowDown':
        case ' ': // Space
        case 'Enter':
          e.preventDefault();
          toggleMenu(true);
          break;
        default:
          break;
      }

      return;
    }

    switch (e.key) {
      case 'Up':
      case 'ArrowUp': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx > 0) {
          setFocusedOption(finalOptions[idx - 1]);
        }

        return;
      }
      case 'Down':
      case 'ArrowDown': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx < finalOptions.length - 1) {
          setFocusedOption(finalOptions[idx + 1]);
        }

        return;
      }
      case 'Esc':
      case 'Escape':
        e.preventDefault();
        handleBlur();
        toggleMenu(false);
        toggleRef.current && toggleRef.current.focus();

        return;
      case 'PageUp': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx - SKIP_AMOUNT <= 0) {
          setFocusedOption(finalOptions[0]);
        } else {
          setFocusedOption(finalOptions[idx - SKIP_AMOUNT]);
        }

        return;
      }
      case 'PageDown': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx + SKIP_AMOUNT >= finalOptions.length - 1) {
          setFocusedOption(finalOptions[finalOptions.length - 1]);
        } else {
          setFocusedOption(finalOptions[idx + SKIP_AMOUNT]);
        }

        break;
      }
      default:
    }
  };

  function isOptionFocused(o: Data) {
    return getValue(o) === getValue(focusedOption);
  }

  function isOptionSelected(o: Data) {
    return getValue(o) === curr;
  }

  function isOptionDisabled(o: Data) {
    if (disableOptionWhen) {
      return disableOptionWhen(o);
    }

    return false;
  }

  function getValue(o: Data | null) {
    if (!o) {
      return '';
    }

    if (optionValue) return optionValue(o);

    return o.value || '';
  }

  function getLabelText(o: Data) {
    if (optionLabel) return optionLabel(o);

    return o.label || '';
  }

  function getOptionId(o: Data | null) {
    if (!o) return '';

    return `${dropdownId.current}_option_${getValue(o)}`;
  }

  function sortFn(a: Data, b: Data) {
    if (!sort) return 0;

    const aVal = sortOn ? sortOn(a) : getLabelText(a);
    const bVal = sortOn ? sortOn(b) : getLabelText(b);

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    if (typeof aVal === 'string') {
      return sortOrder === 'asc' ? -1 : 1;
    }

    if (typeof bVal === 'string') {
      return sortOrder === 'asc' ? 1 : -1;
    }

    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  }

  const PlaceholderMemo = React.useMemo(
    () => (overrideProps: { children?: React.ReactNode }) => (
      <Placeholder>{overrideProps.children || placeholder}</Placeholder>
    ),
    [],
  );

  return (
    <div
      role="none"
      ref={containerRef}
      className={cn('selectContainer')}
      onKeyDown={handleKeyDownOpenDialog}
      style={style}
    >
      {/* Hidden input for native select behavior */}
      <input
        ref={hiddenInputRef}
        type="text"
        tabIndex={-1}
        id={id}
        name={name}
        onBlur={onBlur}
        onChange={(e) => {
          // I don't know why yet, but selectedOption doesn't always get updated in time so I have to re-find here.
          const data = options.find((o) => getValue(o) === e.target.value) || null;
          onChange(e, { data, label: data ? getLabelText(data) : '', value: e.target.value });
        }}
        className={cn('hiddenInputForFiringEvents', 'visuallyHidden')}
        disabled={selectDisabled}
        aria-disabled={selectDisabled}
        aria-readonly={readOnly}
        aria-hidden
      />

      <Popover.Root open={isOpen}>
        <Popover.Trigger asChild>
          <div
            data-testid={`select-${id || name}-trigger`}
            ref={toggleRef}
            tabIndex={selectDisabled ? -1 : 0}
            onKeyDown={() => {}}
            onClick={() => toggleMenu(!isOpen)}
            onFocus={(e: React.FocusEvent<HTMLElement>) => {
              onFocus(e);
            }}
            onBlur={() => {
              // blur triggered not triggered consistently on modal attribute
              if (!isOpen) {
                handleBlur();
              }
            }}
            role="combobox"
            aria-disabled={selectDisabled}
            aria-readonly={readOnly}
            aria-haspopup="listbox"
            aria-controls={`${dropdownId.current}_dropdown`}
            aria-labelledby={`${dropdownId.current}_label`}
            aria-expanded={isOpen}
            aria-activedescendant={getOptionId(focusedOption)}
            className={cn('trigger', errorMsg && 'error')}
          >
            {renderSelectedValue ? (
              renderSelectedValue(selectedOption, {
                label: getSelectedLabelText(),
                value: curr,
                Placeholder: PlaceholderMemo,
                disabled: selectDisabled || readOnly,
              })
            ) : (
              <span className={cn('defaultSelectedValue')}>
                {renderIcon && renderIcon(selectedOption, { Icon })}
                <span className={cn('selectedValueText')}>{curr ? getSelectedLabelText() : <PlaceholderMemo />}</span>
              </span>
            )}

            <div className={cn('triggerControls')}>
              {showClear && curr && !readOnly && !selectDisabled && (
                // TODO: keyboard events
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(null);
                  }}
                  data-custom="true"
                  data-testid={`select-${id || name}-clear`}
                  className={cn('clearButton')}
                  disabled={selectDisabled}
                >
                  <MaterialSymbol icon="close" size="16px" />
                </button>
              )}
              {!readOnly && (
                <Icon
                  icon={loading ? 'loaderCropped' : 'angleDown'}
                  height="14px"
                  width="14px"
                  className={cn('caret', selectDisabled && 'disabled')}
                />
              )}
            </div>
          </div>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            data-testid={`select-${id || name}-dropdown`}
            sideOffset={8}
            align={alignDropdown}
            ref={(e) => {
              trapFocus(e);

              if (e instanceof HTMLDivElement) {
                dropdownRef.current = e;
              }
            }}
            className={cn('dropdown', dropdownWidth === 'trigger' && 'fitToTrigger')}
            tabIndex={-1}
            aria-hidden={!isOpen}
            aria-disabled={selectDisabled}
            style={style}
            onWheel={(e) => {
              // allow dropdown scrolling in drawers/dialogs/etc
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // allow dropdown scrolling in drawers/dialogs/etc
              e.stopPropagation();
            }}
          >
            {filterable && (
              <div className={cn('filterContainer')}>
                <input
                  ref={(input) => input?.focus()}
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                  }}
                  placeholder={t('Select.filterablePlaceholder', { defaultValue: 'Search...' }) || ''}
                  readOnly={readOnly}
                  aria-label={`Search in ${name} options`}
                  aria-readonly={readOnly}
                />
              </div>
            )}

            {renderDropdownHeader &&
              renderDropdownHeader({
                close: () => {
                  handleBlur();
                  toggleMenu(false);
                },
              })}

            {finalOptions.length === 0 && !loading && <p className={cn('defaultOption')}>{emptyFilterMsg}</p>}

            <ul
              role="listbox"
              id={`${dropdownId.current}_dropdown`}
              aria-labelledby={`${dropdownId.current}_label`}
              tabIndex={-1}
              aria-multiselectable={false}
              className={cn('options', 'scrollbar')}
            >
              {loading && (
                <>
                  {Array.from({ length: 4 }).map((_, idx) => (
                    <li key={idx} className={cn('skeletonOption')}>
                      <Skeleton height={20} baseColor="#D9D9D9" />
                    </li>
                  ))}
                </>
              )}
              {!loading &&
                finalOptions.map((o) => {
                  const selected = isOptionSelected(o);
                  const focused = isOptionFocused(o);
                  const val = getValue(o);
                  const optionDisabled = isOptionDisabled(o);

                  let defaultPrevented = false;

                  const preventDefault = () => {
                    defaultPrevented = true;
                  };

                  if (renderOption) {
                    return (
                      <React.Fragment key={`${val}__${getLabelText(o)}`}>
                        {renderOption(o, {
                          focused,
                          selected,
                          disabled: optionDisabled,
                          preventDefault,
                          filter,
                          close: () => {
                            handleBlur();
                            toggleMenu(false);
                          },
                          /**
                           * provide the container to the render prop with various
                           * props pre-configured (id, selected, focused, etc)
                           */
                          Option: React.memo<OptionContainerProps>((propsOverride) => (
                            <SelectOption
                              {...propsOverride}
                              id={getOptionId(o)}
                              selected={selected}
                              focused={focused}
                              disabled={optionDisabled}
                              className={cn('customOption', propsOverride.className)}
                              onMouseOver={() => {
                                // setFocusedOption(o); TODO: fix double click issue on mobile issue
                              }}
                              onSelect={() => {
                                propsOverride.onClick && propsOverride.onClick();
                                log({
                                  message: `${val} was selected for ${name}`,
                                  payload: { name, value: val },
                                  actionType: 'Option',
                                });
                                if (!defaultPrevented) handleChange(val);
                              }}
                              onKeyDown={(e) => {
                                if (!isOpen) return;

                                const spaceIsForSelecting = e.key === ' ' && !filterable;

                                if (e.key === 'Enter' || spaceIsForSelecting) {
                                  e.preventDefault();
                                  propsOverride.onClick && propsOverride.onClick();

                                  if (!defaultPrevented) handleChange(val);
                                }
                              }}
                            />
                          )),
                          SelectedIcon: React.memo((propsOverride) => (
                            <Icon icon="checkCircleFilled" width="24px" height="24px" {...propsOverride} />
                          )),
                        })}
                      </React.Fragment>
                    );
                  }

                  return (
                    <SelectOption
                      key={val}
                      id={getOptionId(o)}
                      selected={selected}
                      focused={focused}
                      disabled={optionDisabled}
                      onMouseOver={() => {
                        setFocusedOption(o);
                      }}
                      onSelect={() => {
                        !optionDisabled && handleChange(val);
                        log({
                          message: `${val} was selected for ${name}`,
                          payload: { name, value: val },
                          actionType: 'Option',
                        });
                      }}
                      onKeyDown={(e) => {
                        // We still want to prevent default, even if the option is disabled.
                        // Just don't fire the change event.
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          !optionDisabled && handleChange(val);
                        }

                        if (e.key === ' ' && !filterable) {
                          e.preventDefault();
                          !optionDisabled && handleChange(val);
                        }
                      }}
                    >
                      {getLabelText(o)}

                      {selected && <Icon icon="checkCircleFilled" width="24px" height="24px" />}
                    </SelectOption>
                  );
                })}
            </ul>

            {renderDropdownFooter &&
              renderDropdownFooter({
                close: () => {
                  handleBlur();
                  toggleMenu(false);
                },
              })}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <ErrorText error={errorMsg} size="sm" />
    </div>
  );
}

export default Select2;

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <span tabIndex={-1} className={cn('placeholder')}>
      {children}
    </span>
  );
}
